import { useState, useEffect } from "react";

import { DateTime } from "luxon";
import { useForm } from "react-hook-form";

import { IPart } from "../../../models/Part";
import useShipping from "../../../data/useShipping";
import LoadingWheel from "../../shared/LoadingWheel";
import SelectInput from "../../shared/Inputs/SelectInput";
import { ShippingQuoteTable } from "./ShippingQuoteTable";
import {
  IProoviaSlot,
  IShippingAddress,
  IShippingDimensions,
  IShippingQuote,
} from "../../../models/Shipping";
import ComboBoxInput from "../../shared/Inputs/ComboBoxInput";
import LoadingTable from "../../shared/LoadingTable";
import LoadingPulse from "../../shared/LoadingPulse";

interface ProoviaShippingProps {
  part: IPart;
  address: IShippingAddress;
  dimensions: IShippingDimensions;
  onProoviaItemSelect: (item: number) => void;
  onCollectionSlotSelect: (date: string) => void;
  onDeliverySlotSelect: (date: string) => void;
  onServiceSelect: (service: string) => void;
  selectedService?: string;
}

export function ProoviaShipping({
  part,
  address,
  dimensions,
  onProoviaItemSelect,
  onCollectionSlotSelect,
  onDeliverySlotSelect,
  onServiceSelect,
  selectedService,
}: ProoviaShippingProps) {
  const { register, watch, setValue, getValues } = useForm<{
    prooviaItemId: number;
    collectionSlot: number | string;
    deliverySlot: number | string;
  }>({
    defaultValues: { prooviaItemId: -1, collectionSlot: -1, deliverySlot: -1 },
  });
  const { prooviaItems, quotes, prooviaCollectionDates, prooviaDeliveryDates } =
    useShipping(part);

  const [quotesLoading, setQuotesLoading] = useState(false);
  const [shippingQuotes, setShippingQuotes] = useState<IShippingQuote[]>();

  const [showCollectionSlots, setShowCollectionSlots] = useState(false);
  const [collectionSlotsLoading, setCollectionSlotsLoading] = useState(false);
  const [collectionSlots, setCollectionSlots] = useState<IProoviaSlot[]>();
  const [selectedCollectionSlot, setSelectedCollectionSlot] = useState<string>();

  const [showDeliverySlots, setShowDeliverySlots] = useState(false);
  const [deliverySlotsLoading, setDeliverySlotsLoading] = useState(false);
  const [deliverySlots, setDeliverySlots] = useState<IProoviaSlot[]>();
  const [selectedDeliverySlot, setSelectedDeliverySlot] = useState<string>();

  const [error, setError] = useState<string>();
  const [hasAutoSelectedService, setHasAutoSelectedService] = useState(false);

  // Validate address fields (addressLine1, postcode, fullName)
  useEffect(() => {
    if (
      !address ||
      !address.addressLine1 ||
      address.addressLine1 === "" ||
      !address.postcode ||
      address.postcode === "" ||
      !address.fullName ||
      address.fullName === ""
    ) {
      setError("Please ensure Shipping address (Address Line 1, Postcode, and Name) are filled in");
    } else {
      setError(undefined);
    }
  }, [address]);

  const getQuotes = () => {
    setQuotesLoading(true);
    quotes.mutate(
      {
        ...address,
        ...dimensions,
        partDataId: part.id,
        prooviaItemId: getValues("prooviaItemId"),
        providerId: "PROOVIA"
      },
      {
        onSuccess: (res) => {
          setShippingQuotes(res.data);
        },
        onSettled: () => {
          setQuotesLoading(false);
        },
      }
    );
  };

  // Auto-select service if there's only 1 quote
  useEffect(() => {
    if (shippingQuotes && shippingQuotes.length === 1 && !selectedService && !quotesLoading && !hasAutoSelectedService) {
      const singleService = shippingQuotes[0].service;
      setHasAutoSelectedService(true);
      // Ensure collection slots are shown immediately
      setShowCollectionSlots(true);
      // Call the handler which will trigger collection slots loading
      onServiceSelect(singleService);
      getCollectionSlots();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shippingQuotes, quotesLoading, selectedService, hasAutoSelectedService]);

  // Auto-select earliest collection date when slots are loaded
  const currentCollectionSlot = watch("collectionSlot");
  useEffect(() => {
    if (collectionSlots && collectionSlots.length > 0 && currentCollectionSlot === -1 && !collectionSlotsLoading && showCollectionSlots) {
      // Sort by date and get the earliest one
      const sortedSlots = [...collectionSlots].sort((a, b) => 
        DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis()
      );
      const earliestDate = sortedSlots[0].date;
      // Update form value and call parent callback directly
      setValue("collectionSlot", earliestDate as any);
      onCollectionSlotSelect(earliestDate);
      setSelectedCollectionSlot(earliestDate);
      getDeliverySlots();
      setShowDeliverySlots(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionSlots, collectionSlotsLoading, showCollectionSlots, currentCollectionSlot]);

  // Auto-select earliest delivery date when slots are loaded
  const currentDeliverySlot = watch("deliverySlot");
  useEffect(() => {
    if (deliverySlots && deliverySlots.length > 0 && currentDeliverySlot === -1 && !deliverySlotsLoading && showDeliverySlots) {
      // Sort by date and get the earliest one
      const sortedSlots = [...deliverySlots].sort((a, b) => 
        DateTime.fromISO(a.date).toMillis() - DateTime.fromISO(b.date).toMillis()
      );
      const earliestDate = sortedSlots[0].date;
      // Update form value and call parent callback directly
      setValue("deliverySlot", earliestDate as any);
      onDeliverySlotSelect(earliestDate);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deliverySlots, deliverySlotsLoading, showDeliverySlots, currentDeliverySlot]);

  const handleTypeChange = (_: any, val: any) => {
    if (val === -1 || !val) {
      return;
    }

    // Reset auto-selection flags when item type changes
    setHasAutoSelectedService(false);
    setShowCollectionSlots(false);
    setShowDeliverySlots(false);
    setValue("prooviaItemId", val);
    // Always set the Proovia Item Id, even if we can't get quotes yet
    onProoviaItemSelect(val);

    // Only get quotes if address validation passes
    if (
      address &&
      address.addressLine1 &&
      address.addressLine1 !== "" &&
      address.postcode &&
      address.postcode !== "" &&
      address.fullName &&
      address.fullName !== ""
    ) {
      getQuotes();
    }
  };

  const getCollectionSlots = () => {
    // Validate address before making API call
    if (!address || !address.postcode || address.postcode === "") {
      setError("Please ensure Shipping address postcode is filled in");
      return;
    }
    
    setCollectionSlotsLoading(true);
    prooviaCollectionDates.mutate(
      {
        from: "DT2 9PW",
        to: address.postcode.replace(/^(.*)(\d)/, "$1 $2"),
      },
      {
        onSuccess: (res) => {
          setCollectionSlots(res.data);
        },
        onSettled: () => {
          setCollectionSlotsLoading(false);
        },
      }
    );
  };

  const handleServiceSelect = (service: string) => {
    getCollectionSlots();
    onServiceSelect(service);
    setShowCollectionSlots(true);
  };

  const getDeliverySlots = () => {
    // Validate address before making API call
    if (!address || !address.postcode || address.postcode === "") {
      setError("Please ensure Shipping address postcode is filled in");
      return;
    }
    
    setDeliverySlotsLoading(true);
    prooviaDeliveryDates.mutate(
      {
        from: "DT2 9PW",
        to: address.postcode.replace(/^(.*)(\d)/, "$1 $2"),
        collectionDate: String(getValues("collectionSlot")),
      },
      {
        onSuccess: (res) => {
          setDeliverySlots(res.data);
        },
        onSettled: () => {
          setDeliverySlotsLoading(false);
        },
      }
    );
  };

  const handleCollectionSlotChange = (val: any) => {
    if (val === -1) {
      return;
    }
    
    // Handle side effects (form value is already updated by wrapper)
    onCollectionSlotSelect(val);
    setSelectedCollectionSlot(val);
    getDeliverySlots();
    setShowDeliverySlots(true);
  };

  const handleDeliverySlotChange = (val: any) => {
    if (val === -1) {
      return;
    }
    
    // Handle side effects (form value is already updated by wrapper)
    onDeliverySlotSelect(val);
  };

  // Wrapper functions that update form value first, then call custom handlers
  const collectionSlotSetValue = (_registerName: string, val: any) => {
    if (val === -1) {
      return;
    }
    // Update form value immediately so Headless UI Listbox closes
    setValue("collectionSlot", val as any);
    // Then call custom handler for side effects
    handleCollectionSlotChange(val);
  };

  const deliverySlotSetValue = (_registerName: string, val: any) => {
    if (val === -1) {
      return;
    }
    // Update form value immediately so Headless UI Listbox closes
    setValue("deliverySlot", val as any);
    // Then call custom handler for side effects
    handleDeliverySlotChange(val);
  };

  return (
    <div>
      {prooviaItems.isLoading ? (
        <LoadingPulse />
      ) : (
        <div className="space-y-4">
          {/* <SelectInput
            label="Item Type"
            register={register}
            registerName="prooviaItemId"
            setValue={handleTypeChange}
            options={[
              { id: -1, name: "Choose..." },
              ...prooviaItems.data!.data,
            ]}
            watch={watch}
          /> */}
          <ComboBoxInput
            label="Item Type"
            register={register}
            registerName="prooviaItemId"
            setValue={(_: any, val: any) => {
              handleTypeChange(_, val?.id);
            }}
            options={[
              { id: -1, name: "Choose..." },
              ...prooviaItems.data!.data,
            ]}
            searchField={"name"}
          />
          {error && (
            <div className="col-span-4 bg-red-100 text-red-600 px-4 py-2 rounded">
              {error}
            </div>
          )}
          {watch("prooviaItemId") !== -1 &&
            (quotesLoading ? (
              <LoadingTable headers={["Service", "Price"]} rows={3} />
            ) : (
              <div>
                <ShippingQuoteTable
                  shippingQuotes={shippingQuotes!}
                  onServiceSelect={handleServiceSelect}
                  selectedService={selectedService}
                />
              </div>
            ))}
          {showCollectionSlots ? (
            collectionSlotsLoading ? (
              <LoadingPulse />
            ) : (
              <SelectInput
                label="Collection Date"
                register={register}
                registerName="collectionSlot"
                setValue={collectionSlotSetValue}
                options={[
                  { id: -1, name: "Choose..." },
                  ...collectionSlots!.map((d) => ({
                    id: d.date,
                    name: DateTime.fromISO(d.date).toFormat("dd/MM/yyyy"),
                  })),
                ]}
                watch={watch}
              />
            )
          ) : (
            <div />
          )}
          {showDeliverySlots ? (
            deliverySlotsLoading ? (
              <LoadingPulse />
            ) : (
              <SelectInput
                label="Delivery Date"
                register={register}
                registerName="deliverySlot"
                setValue={deliverySlotSetValue}
                options={[
                  { id: -1, name: "Choose..." },
                  ...deliverySlots!.map((d) => ({
                    id: d.date,
                    name: DateTime.fromISO(d.date).toFormat("dd/MM/yyyy"),
                  })),
                ]}
                watch={watch}
              />
            )
          ) : (
            <div />
          )}
        </div>
      )}
    </div>
  );
}
